import { useState, useEffect } from 'react';

/**
 * Custom hook for responsive behavior using window.matchMedia
 * 
 * @param query - Media query string (e.g., "(max-width: 768px)")
 * @returns boolean indicating if the media query matches
 * 
 * @example
 * const isMobile = useMediaQuery('(max-width: 768px)');
 * const isTablet = useMediaQuery('(min-width: 769px) and (max-width: 1024px)');
 */
export const useMediaQuery = (query: string): boolean => {
    // SSR safety: return false if window is undefined
    const getMatches = (query: string): boolean => {
        if (typeof window !== 'undefined') {
            // Optional: jest's matchMedia mock returns undefined once
            // resetMocks has cleared it, and a missing answer means "no".
            return window.matchMedia?.(query)?.matches ?? false;
        }
        return false;
    };

    const [matches, setMatches] = useState<boolean>(getMatches(query));

    useEffect(() => {
        // SSR safety check
        if (typeof window === 'undefined') {
            return;
        }

        const mediaQuery = window.matchMedia?.(query);
        if (!mediaQuery) {
            return;
        }

        // Update state when media query changes
        const handleChange = () => {
            setMatches(mediaQuery.matches);
        };

        // Modern browsers use addEventListener
        // Older browsers use addListener (deprecated but still supported)
        if (mediaQuery.addEventListener) {
            mediaQuery.addEventListener('change', handleChange);
        } else {
            // Fallback for older browsers
            mediaQuery.addListener(handleChange);
        }

        // Initial check
        setMatches(mediaQuery.matches);

        // Cleanup
        return () => {
            if (mediaQuery.removeEventListener) {
                mediaQuery.removeEventListener('change', handleChange);
            } else {
                // Fallback for older browsers
                mediaQuery.removeListener(handleChange);
            }
        };
    }, [query]);

    return matches;
};
